// Voice service with updated voice configurations
export const voiceService = {
  // Get available voices for the form
  getAvailableVoices: async () => {
    console.log('🎵 Loading available voices...');
    
    // Return voices with valid Vapi voice IDs
    const voices = [
      // Valid Vapi voices only
      { voiceId: 'Elliot', name: 'Elliot (Male, American)', provider: 'vapi' },
      { voiceId: 'Kylie', name: 'Kylie (Female, American)', provider: 'vapi' },
      { voiceId: 'Rohan', name: 'Rohan (Male, American)', provider: 'vapi' },
      { voiceId: 'Lily', name: 'Lily (Female, American)', provider: 'vapi' },
      { voiceId: 'Savannah', name: 'Savannah (Female, American)', provider: 'vapi' },
      { voiceId: 'Hana', name: 'Hana (Female, American)', provider: 'vapi' },
      { voiceId: 'Neha', name: 'Neha (Female, American)', provider: 'vapi' },
      { voiceId: 'Cole', name: 'Cole (Male, American)', provider: 'vapi' },
      { voiceId: 'Harry', name: 'Harry (Male, American)', provider: 'vapi' },
      { voiceId: 'Paige', name: 'Paige (Female, American)', provider: 'vapi' },
      { voiceId: 'Spencer', name: 'Spencer (Male, American)', provider: 'vapi' }
    ];

    console.log('🎵 Loaded voices:', voices.length);
    return voices;
  }
};

// Assistant service
export const assistantService = {
  // Build Vapi call configuration from assistant data
  buildVapiCallConfig: async (assistantData) => {
    console.log('🔧 Building Vapi call config for assistant:', assistantData.name || 'Unnamed');
    console.log('📋 Input assistant data:', assistantData);

    try {
      // Extract voice information
      let voiceConfig;
      const voiceId = assistantData.voice_id || assistantData.voice?.voiceId;
      const voiceName = assistantData.voice_name || assistantData.voice?.name;
      
      if (voiceId) {
        console.log('🎵 Using stored voice:', voiceId, voiceName);
        
        // Map database voice IDs to valid Vapi voice names
        // For valid Vapi voices, map to themselves
        // For legacy/invalid voices, map to valid alternatives
        const voiceMapping = {
          // Valid Vapi voices map to themselves
          'Elliot': 'Elliot',
          'Kylie': 'Kylie', 
          'Rohan': 'Rohan',
          'Lily': 'Lily',
          'Savannah': 'Savannah',
          'Hana': 'Hana',
          'Neha': 'Neha',
          'Cole': 'Cole',
          'Harry': 'Harry',
          'Paige': 'Paige',
          'Spencer': 'Spencer',
          
          // Legacy/invalid voice mappings
          'MF3mGyEYCl7XYWbV9V6O': 'Lily',
          '21m00Tcm4TlvDq8ikWAM': 'Savannah',
          'pNInz6obpgDQGcFmaJgB': 'Elliot',
          'jennifer': 'Lily',
          'sarah': 'Savannah',
          'burt': 'Elliot',
          'charlie': 'Cole',
          'liam': 'Harry'
        };
        
        let finalVoiceId = voiceMapping[voiceId] || voiceId;
        
        if (voiceMapping[voiceId]) {
          console.log('🎵 Mapped voice ID:', voiceId, '->', finalVoiceId);
        } else {
          console.log('🎵 Using direct voice ID:', voiceId);
        }
        
        // Validate against allowed Vapi voices
        const validVapiVoices = ['Elliot', 'Kylie', 'Rohan', 'Lily', 'Savannah', 'Hana', 'Neha', 'Cole', 'Harry', 'Paige', 'Spencer'];
        if (!validVapiVoices.includes(finalVoiceId)) {
          console.log('🎵 Invalid voice ID, using default:', finalVoiceId);
          finalVoiceId = 'Lily'; // Default to Lily
        }
        
        voiceConfig = {
          provider: 'vapi',
          voiceId: finalVoiceId,
          chunkPlan: {
            enabled: true
          }
        };
        
        console.log('🎵 Built voice config:', voiceConfig);
        
      } else {
        console.log('🎵 No voice specified, using default');
        voiceConfig = {
          provider: 'vapi',
          voiceId: 'Lily', // Default reliable voice
          chunkPlan: {
            enabled: true
          }
        };
      }

      console.log('🎵 Final voice config:', voiceConfig);

      // Model configuration
      const model = assistantData.model || 'gpt-4o-mini';
      console.log('🤖 Using model:', model);

      // Model settings
      const temperature = assistantData.temperature || 0.7;
      const maxTokens = assistantData.max_tokens || assistantData.maxTokens || 250;
      console.log('⚙️ Model settings:', { temperature, maxTokens });

      // System message
      const systemMessage = assistantData.system_message || assistantData.systemMessage || 'You are a helpful AI assistant.';
      console.log('📝 System message length:', systemMessage.length);

      // Build the complete Vapi configuration in the exact format required
      const config = {
        transcriber: {
          provider: "deepgram",
          model: "nova-2",
          language: "en"
        },
        model: {
          provider: "openai",
          model: model,
          messages: [
            {
              role: "system",
              content: systemMessage
            }
          ]
        },
        voice: voiceConfig,
        name: assistantData.name || "AI Assistant",
        firstMessage: assistantData.first_message || assistantData.firstMessage || "Hello! How can I help you today?"
      };

      // Add hooks if present (for idle messages)
      if (assistantData.hooks && assistantData.hooks.length > 0) {
        config.hooks = assistantData.hooks;
      }

      console.log('✅ Built Vapi transient assistant config:', JSON.stringify(config, null, 2));

      // Validate the configuration
      if (!config.name || config.name.trim().length === 0) {
        throw new Error('Assistant name is required');
      }
      
      if (!config.model || !config.model.model) {
        throw new Error('Model configuration is required');
      }
      
      if (!config.voice || !config.voice.voiceId) {
        throw new Error('Voice configuration is required');
      }

      // Validate voice ID is allowed
      const validVapiVoices = ['Elliot', 'Kylie', 'Rohan', 'Lily', 'Savannah', 'Hana', 'Neha', 'Cole', 'Harry', 'Paige', 'Spencer'];
      if (!validVapiVoices.includes(config.voice.voiceId)) {
        throw new Error(`Invalid voice ID: ${config.voice.voiceId}. Must be one of: ${validVapiVoices.join(', ')}`);
      }
      
      if (!config.model.messages || config.model.messages.length === 0) {
        throw new Error('System message is required');
      }

      console.log('✅ Configuration validation passed');
      return config;

    } catch (error) {
      console.error('❌ Error building Vapi config:', error);
      throw new Error(`Failed to build call configuration: ${error.message}`);
    }
  },

  // Get available voices - proxy to voiceService
  getAvailableVoices: async () => {
    return await voiceService.getAvailableVoices();
  },

  // Normalize data from frontend format to database format
  normalizeForDatabase: (frontendData) => {
    console.log('🔄 Normalizing frontend data for database:', frontendData);
    
    let voiceId, voiceName;
    
    if (frontendData.voice && typeof frontendData.voice === 'object') {
      voiceId = frontendData.voice.voiceId;
      voiceName = frontendData.voice.name;
      console.log('🎵 Extracted voice from object:', { voice_id: voiceId, voice_name: voiceName });
    } else if (typeof frontendData.voice === 'string') {
      voiceId = frontendData.voice;
      voiceName = frontendData.voiceName || voiceId;
      console.log('🎵 Extracted voice from string:', { voice_id: voiceId, voice_name: voiceName });
    } else {
      voiceId = frontendData.voiceId || frontendData.voice_id || 'Lily';
      voiceName = frontendData.voiceName || frontendData.voice_name || 'Lily (Female, American)';
      console.log('🎵 Using fallback voice:', { voice_id: voiceId, voice_name: voiceName });
    }

    const normalized = {
      name: frontendData.name,
      description: frontendData.description || null,
      model: frontendData.model || 'gpt-4o-mini',
      voice_id: voiceId,
      voice_name: voiceName,
      system_message: frontendData.systemMessage || frontendData.system_message || 'You are a helpful AI assistant.',
      first_message: frontendData.firstMessage || frontendData.first_message || 'Hello! How can I help you today?',
      temperature: frontendData.temperature || 0.7,
      max_tokens: frontendData.maxTokens || frontendData.max_tokens || 250,
      vapi_assistant_id: frontendData.vapi_assistant_id || null,
      hooks: frontendData.hooks || [],
      created_at: frontendData.created_at || new Date().toISOString(),
      updated_at: new Date().toISOString()
    };

    console.log('✅ Normalized data for database:', normalized);
    return normalized;
  },

  // Normalize data from database format to frontend format
  normalizeForFrontend: (databaseData) => {
    console.log('🔄 Normalizing database data for frontend:', databaseData);
    
    const voiceObject = {
      voiceId: databaseData.voice_id || 'Lily',
      name: databaseData.voice_name || 'Lily (Female, American)',
      provider: 'vapi'
    };
    
    console.log('🎵 Created voice object for frontend:', voiceObject);

    const normalized = {
      id: databaseData.id,
      name: databaseData.name,
      description: databaseData.description,
      voice: voiceObject,
      first_message: databaseData.first_message || 'Hello! How can I help you today?',
      system_message: databaseData.system_message || 'You are a helpful AI assistant.',
      model: databaseData.model || 'gpt-4o-mini',
      temperature: databaseData.temperature || 0.7,
      max_tokens: databaseData.max_tokens || 250,
      vapi_assistant_id: databaseData.vapi_assistant_id,
      hooks: databaseData.hooks || [],
      created_at: databaseData.created_at,
      updated_at: databaseData.updated_at,
      // Additional fields for compatibility
      voiceId: databaseData.voice_id,
      voiceName: databaseData.voice_name,
      firstMessage: databaseData.first_message,
      systemMessage: databaseData.system_message,
      maxTokens: databaseData.max_tokens,
      voice_id: databaseData.voice_id,
      voice_name: databaseData.voice_name
    };

    console.log('✅ Normalized data for frontend:', normalized);
    return normalized;
  },

  // Create a new assistant
  createAssistant: async (assistantData, userId) => {
    console.log('📝 Creating new assistant for user:', userId);
    console.log('📋 Assistant data:', assistantData);

    try {
      // Import supabase here to avoid circular dependency
      const { default: supabase } = await import('../lib/supabase.js');
      
      // Normalize the data for database storage
      const normalizedData = assistantService.normalizeForDatabase(assistantData);
      
      console.log('📤 Normalized data:', normalizedData);

      // Insert into Supabase
      const { data, error } = await supabase
        .from('assistants_va2025')
        .insert([{
          ...normalizedData,
          user_id: userId
        }])
        .select()
        .single();

      if (error) {
        console.error('❌ Supabase error:', error);
        throw new Error(`Failed to save assistant: ${error.message}`);
      }

      console.log('✅ Assistant created in Supabase:', data);
      
      // Return normalized data for frontend
      return assistantService.normalizeForFrontend(data);

    } catch (error) {
      console.error('❌ Error creating assistant:', error);
      throw new Error(`Failed to create assistant: ${error.message}`);
    }
  },

  // Update an existing assistant
  updateAssistant: async (assistantId, assistantData, userId) => {
    console.log('📝 Updating assistant:', assistantId, 'for user:', userId);
    console.log('📋 Update data:', assistantData);

    try {
      // Import supabase here to avoid circular dependency
      const { default: supabase } = await import('../lib/supabase.js');
      
      // Normalize the data for database storage
      const normalizedData = assistantService.normalizeForDatabase(assistantData);
      
      console.log('📤 Normalized update data:', normalizedData);

      // Update in Supabase
      const { data, error } = await supabase
        .from('assistants_va2025')
        .update(normalizedData)
        .eq('id', assistantId)
        .eq('user_id', userId)
        .select()
        .single();

      if (error) {
        console.error('❌ Supabase error:', error);
        throw new Error(`Failed to update assistant: ${error.message}`);
      }

      console.log('✅ Assistant updated in Supabase:', data);
      
      // Return normalized data for frontend
      return assistantService.normalizeForFrontend(data);

    } catch (error) {
      console.error('❌ Error updating assistant:', error);
      throw new Error(`Failed to update assistant: ${error.message}`);
    }
  },

  // Get all assistants for a user
  getUserAssistants: async (userId) => {
    console.log('📋 Fetching assistants for user:', userId);

    try {
      // Import supabase here to avoid circular dependency
      const { default: supabase } = await import('../lib/supabase.js');

      const { data, error } = await supabase
        .from('assistants_va2025')
        .select('*')
        .eq('user_id', userId)
        .order('created_at', { ascending: false });

      if (error) {
        console.error('❌ Supabase error:', error);
        throw new Error(`Failed to fetch assistants: ${error.message}`);
      }

      console.log('✅ Fetched assistants:', data);
      
      // Normalize all assistants for frontend
      return data.map(assistant => assistantService.normalizeForFrontend(assistant));

    } catch (error) {
      console.error('❌ Error fetching assistants:', error);
      throw new Error(`Failed to fetch assistants: ${error.message}`);
    }
  },

  // Get all assistants for a user (alias for compatibility)
  getAssistants: async (userId) => {
    return await assistantService.getUserAssistants(userId);
  },

  // Delete an assistant
  deleteAssistant: async (assistantId, userId) => {
    console.log('🗑️ Deleting assistant:', assistantId, 'for user:', userId);

    try {
      // Import supabase here to avoid circular dependency
      const { default: supabase } = await import('../lib/supabase.js');

      const { error } = await supabase
        .from('assistants_va2025')
        .delete()
        .eq('id', assistantId)
        .eq('user_id', userId);

      if (error) {
        console.error('❌ Supabase error:', error);
        throw new Error(`Failed to delete assistant: ${error.message}`);
      }

      console.log('✅ Assistant deleted from Supabase');
      return true;

    } catch (error) {
      console.error('❌ Error deleting assistant:', error);
      throw new Error(`Failed to delete assistant: ${error.message}`);
    }
  }
};

// Export as default for compatibility with existing imports
export default assistantService;